/**
 * Contracts API Client
 * US-CONT-005: View Expiring Contracts Alert
 * Handles all API calls to commerce-service for contracts
 */

import { authManager } from '@/services/api';
import type {
  ExpiringContract,
  ExpiringContractsResponse,
  ExpiringContractsSummary,
  ExpiringContractsQueryParams,
  RenewalRequest,
  RenewalResponse,
  ApiResponse
} from '../types/expiringContracts.types';

/**
 * Get Commerce Service URL from environment
 * Use relative path in dev mode for Vite proxy
 */
const isDevelopment = import.meta.env.DEV;
const COMMERCE_SERVICE_URL = isDevelopment
  ? '/commerce-service'
  : (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service');

/**
 * Contracts API Client
 */
export const contractsApi = {
  /**
   * Get expiring contracts with filters, sort, and pagination
   * @param params - Query parameters
   * @returns Promise with paginated contracts response
   */
  async getExpiringContracts(params: Partial<ExpiringContractsQueryParams>): Promise<ApiResponse<ExpiringContractsResponse>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();

      // Add filters
      if (params.search) queryParams.append('search', params.search);
      if (params.urgency && params.urgency.length > 0) {
        params.urgency.forEach(u => queryParams.append('urgency', u));
      }
      if (params.contractType && params.contractType.length > 0) {
        params.contractType.forEach(t => queryParams.append('contractType', t));
      }
      if (params.status && params.status.length > 0) {
        params.status.forEach(s => queryParams.append('status', s));
      }
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.days !== undefined) queryParams.append('days', params.days.toString());

      // Add sort
      if (params.field) queryParams.append('sortBy', params.field);
      if (params.direction) queryParams.append('sortDirection', params.direction.toUpperCase());

      // Add pagination
      if (params.page !== undefined) queryParams.append('page', params.page.toString());
      if (params.size !== undefined) queryParams.append('size', params.size.toString());

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/expiring?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch expiring contracts');
      }

      const result = await response.json();

      // Handle response structure
      const data = result.data || result;

      return {
        success: true,
        data: {
          content: data.content || [],
          totalElements: data.totalElements || 0,
          totalPages: data.totalPages || 0,
          currentPage: data.number !== undefined ? data.number : data.currentPage || 0,
          pageSize: data.size !== undefined ? data.size : data.pageSize || 20,
          hasNext: data.hasNext !== undefined ? data.hasNext : !data.last,
          hasPrevious: data.hasPrevious !== undefined ? data.hasPrevious : !data.first
        }
      };
    } catch (error) {
      console.error('Error fetching expiring contracts:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch expiring contracts'
      };
    }
  },

  /**
   * Get summary of expiring contracts for dashboard
   * @param days - Number of days threshold (default: 30)
   * @returns Promise with summary data
   */
  async getExpiringContractsSummary(days: number = 30): Promise<ApiResponse<ExpiringContractsSummary>> {
    try {
      const token = authManager.getToken();
      const queryParams = new URLSearchParams();
      queryParams.append('days', days.toString());

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/expiring/summary?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch summary');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        success: true,
        data: {
          totalExpiring: data.totalExpiring || 0,
          criticalCount: data.criticalCount || 0,
          warningCount: data.warningCount || 0,
          normalCount: data.normalCount || 0,
          expiredCount: data.expiredCount || 0,
          lastUpdated: data.lastUpdated || new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error fetching summary:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch summary'
      };
    }
  },

  /**
   * Get contract by ID
   * @param id - Contract ID
   * @returns Promise with contract details
   */
  async getContractById(id: string): Promise<ApiResponse<ExpiringContract>> {
    try {
      const token = authManager.getToken();

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/${id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch contract');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error fetching contract:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch contract'
      };
    }
  },

  /**
   * Initiate contract renewal
   * @param request - Renewal request payload
   * @returns Promise with renewal response
   */
  async initiateRenewal(request: RenewalRequest): Promise<ApiResponse<RenewalResponse>> {
    try {
      const token = authManager.getToken();

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/${request.contractId}/renew`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          },
          body: JSON.stringify({
            newStartDate: request.newStartDate,
            newEndDate: request.newEndDate,
            contractValue: request.contractValue,
            notes: request.notes
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to renew contract');
      }

      const result = await response.json();
      const data = result.data || result;

      return {
        success: true,
        data: {
          success: true,
          newContractId: data.newContractId || data.id,
          newContractNumber: data.newContractNumber || data.contractNumber,
          message: data.message || 'Contract renewed successfully'
        }
      };
    } catch (error) {
      console.error('Error renewing contract:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to renew contract'
      };
    }
  },

  /**
   * Download contract document
   * @param contractId - Contract ID
   * @returns Promise with blob for download
   */
  async downloadContract(contractId: string): Promise<Blob> {
    try {
      const token = authManager.getToken();

      const response = await fetch(
        `${COMMERCE_SERVICE_URL}/api/v1/contracts/${contractId}/download`,
        {
          method: 'GET',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download contract');
      }

      const blob = await response.blob();

      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-${contractId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return blob;
    } catch (error) {
      console.error('Error downloading contract:', error);
      throw error;
    }
  }
};

/**
 * Query keys factory for React Query
 * Provides consistent cache key generation
 */
export const contractQueryKeys = {
  all: ['contracts'] as const,
  expiring: () => [...contractQueryKeys.all, 'expiring'] as const,
  expiringList: (params?: Partial<ExpiringContractsQueryParams>) =>
    [...contractQueryKeys.expiring(), 'list', params] as const,
  expiringSummary: (days?: number) =>
    [...contractQueryKeys.expiring(), 'summary', days] as const,
  detail: (id: string) => [...contractQueryKeys.all, 'detail', id] as const
};
